import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import _ from 'lodash';
import i18n from '../../i18n';
import { apiManage_rbp } from '../../request';
import GlobalNotification from '../../component/common/snackbar';
import setLoading from '../../component/linear-determinate';
import CommonInput from '../../component/form/common-input.component';
import type { SelectOption } from '../../component/form/base-input.component';
import { IOSSwitch } from '../../component/switch/IOSSwitch';
import { RbpCloudSubFunctionAuxiliaryModeContext } from './rbp-cloud-sub-function-auxiliary-mode-context';
import SubFunctionProjectSettingSaveButton from './sub-function-project-setting-save-button.component';
import { CopSettingPageSkeleton } from './sub-function-settings-skeletons.component';
import './scss/cop-setting.page.scss';

/** 纵向表单项间距：与稿面 Frame 427320235 子项 itemSpacing 24 一致（MUI spacing 基数 4） */
const CARD_FIELD_STACK_SPACING = 3;

/** 公网 QOP/COP 扫码相关授权码，与 RBP2 `QOP_AUTH`、RWA/QOP 页一致 */
const QOP_WARRANT_CODES = ['AU1024', 'AU1025', 'AU1026'] as const;

type Props = Record<string, never>;

type State = {
    /** 全页保存中 */
    isSaving: boolean;
    /** 是否具备外网扫码授权（决定设置 137 是否可选 2） */
    hasPublicQopAuth: boolean;
    /** 设置 id 159：背景图全局应用，与 RBP2 COP 页开关语义一致 */
    imageGlobalApply: boolean;
    /** 设置 id 137：QR 扫码方式 0 关 / 1 内网 / 2 外网 */
    qrScanMode: number;
    /** 首包拉取完成前展示骨架 */
    initialPageDataReady: boolean;
};

/**
 * 云 RBP「子功能管理」— COP 设置：背景图全局应用 + QR 扫码方式（与 RBP2 `project-setting-cop` 一致，走 `apiManage_rbp`）。
 * 布局对齐 Figma 1897:22638 与 KWA/MOP 内容区。隧道校验在子功能壳页 `HookDecorator` 中统一完成。
 */
class CopSettingPage extends React.Component<Props, State> {
    /** 顶栏「辅助模式」：控制 Figma 次要说明（node 1897:22638） */
    static contextType = RbpCloudSubFunctionAuxiliaryModeContext;
    /** 由 `contextType` 注入 */
    context!: React.ContextType<typeof RbpCloudSubFunctionAuxiliaryModeContext>;

    /** 防止保存重复提交 */
    private saveLocked = false;

    public constructor(props: Props) {
        super(props);
        this.state = {
            isSaving: false,
            hasPublicQopAuth: false,
            imageGlobalApply: false,
            qrScanMode: 1,
            initialPageDataReady: false,
        };
    }

    public async componentDidMount(): Promise<void> {
        await this.loadPageData();
    }

    /**
     * 判断是否具备公网扫码授权（与 RBP2 `getAuthoQop` 一致）。
     * @returns 是否具备公网授权
     */
    private refreshPublicQopAuth = async (): Promise<boolean> => {
        let has = false;
        const api45 = await apiManage_rbp.getSettingById(45);
        const swCloudKey = String(_.get(api45, '[0].value', '') || '').trim();
        if (swCloudKey) {
            const verifyRes = await apiManage_rbp.getSmartwayCloudKey(swCloudKey);
            const stringId = _.get(verifyRes, ['restaurant', 'string_id']);
            if (stringId && !_.get(verifyRes, 'error')) {
                const data = await apiManage_rbp.getAuthorizationMessage();
                const authorizationMessage = _.get(data, 'warrant_data', '');
                if (_.isArray(authorizationMessage) && authorizationMessage.length > 0) {
                    has = !!authorizationMessage.find((item: { code?: string }) =>
                        QOP_WARRANT_CODES.includes(item?.code as (typeof QOP_WARRANT_CODES)[number]),
                    );
                }
            }
        }
        this.setState({ hasPublicQopAuth: has });
        return has;
    };

    /**
     * 拉取设置 137、159，并按授权裁剪「外网」选项。
     */
    private loadPageData = async (): Promise<void> => {
        try {
            const hasPublicQopAuth = await this.refreshPublicQopAuth();
            const [r137, r159] = await Promise.all([
                apiManage_rbp.getSettingById(137),
                apiManage_rbp.getSettingById(159),
            ]);
            let qrScanMode = Number(_.get(r137, '[0].value', 1));
            if (!hasPublicQopAuth && qrScanMode === 2) {
                qrScanMode = 1;
            }
            const raw159 = _.get(r159, '[0].value');
            const imageGlobalApply = Boolean(Number(raw159 ?? 0));
            this.setState({ qrScanMode, imageGlobalApply });
        } finally {
            this.setState({ initialPageDataReady: true });
        }
    };

    /**
     * 保存设置 137、159。
     */
    private persistSettings = async (): Promise<void> => {
        const { qrScanMode, imageGlobalApply } = this.state;
        setLoading(true);
        const api137 = apiManage_rbp.patchSettingById(137, { stringValue: String(qrScanMode) });
        const api159 = apiManage_rbp.patchSettingById(159, { stringValue: String(Number(!!imageGlobalApply)) });
        const results = await Promise.all([api137, api159]);
        const failed = results.some((res) => _.get(res, 'error'));
        if (failed) {
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        } else {
            GlobalNotification.success(i18n.t('global_text_operate_success'));
        }
        setLoading(false);
    };

    /**
     * 点击保存。
     */
    private onClickSave = async (): Promise<void> => {
        if (this.saveLocked || this.state.isSaving) {
            return;
        }
        this.saveLocked = true;
        this.setState({ isSaving: true });
        try {
            await this.persistSettings();
        } finally {
            this.saveLocked = false;
            this.setState({ isSaving: false });
        }
    };

    /**
     * 修改 QR 扫码方式；无公网授权时禁止选「外网」。
     * @param next - 0 | 1 | 2
     */
    private onChangeQrScanMode = (next: number): void => {
        if (next === 2 && !this.state.hasPublicQopAuth) {
            GlobalNotification.error(i18n.t('cop_setting_page_qr_code_scan_error'));
            return;
        }
        this.setState({ qrScanMode: next });
    };

    /**
     * COP 扫码方式下拉项（`label` 为 i18n key，由 BaseInput 翻译）。
     * @returns 下拉选项
     */
    private getQrScanItems(): SelectOption[] {
        const all: SelectOption[] = [
            { value: '0', label: 'cash_drop_page_off' },
            { value: '1', label: 'qop_setting_page_local_network' },
            { value: '2', label: 'qop_setting_page_data_network', disabled: !this.state.hasPublicQopAuth },
        ];
        return all;
    }

    public render(): React.ReactNode {
        if (!this.state.initialPageDataReady) {
            return <CopSettingPageSkeleton />;
        }
        const { isSaving, imageGlobalApply, qrScanMode } = this.state;
        return (
            <Box className="cop-setting-page">
                {/* 与 Figma 1897:22529 一致：浅灰底圆角容器 + 投影，内嵌页头与白卡 */}
                <Box className="cop-setting-page__surface">
                    <Box className="cop-setting-page__header">
                        <Typography variant="h3" color="primary" className="cop-setting-page__page-title">
                            {i18n.t('cop_setting_page_heading')}
                        </Typography>
                        <SubFunctionProjectSettingSaveButton loading={isSaving} onClick={this.onClickSave} />
                    </Box>

                    <Box className="cop-setting-page__card">
                        <Typography variant="subtitle1" color="primary" className="cop-setting-page__section-title">
                            {i18n.t('cop_setting_page_basic_section_title')}
                        </Typography>
                        <Stack spacing={CARD_FIELD_STACK_SPACING} className="cop-setting-page__card-body">
                            {/* 稿面 1897:22538：横向 gap 32，开关紧邻主副文案右侧 */}
                            <Box className="cop-setting-page__field-row">
                                <Stack spacing={0} className="cop-setting-page__field-label">
                                    <Typography variant="subtitle2" color="text.primary">
                                        {i18n.t('cop_setting_page_image_global_apply')}
                                    </Typography>
                                    {this.context?.auxiliaryModeEnabled ? (
                                        <Typography variant="body2" color="text.secondary">
                                            {i18n.t('cop_setting_page_image_global_apply_hint')}
                                        </Typography>
                                    ) : null}
                                </Stack>
                                <Box className="cop-setting-page__field-control">
                                    <IOSSwitch
                                        checked={imageGlobalApply}
                                        onChange={() => this.setState({ imageGlobalApply: !imageGlobalApply })}
                                        inputProps={{ 'aria-label': i18n.t('cop_setting_page_image_global_apply') }}
                                    />
                                </Box>
                            </Box>
                            {/* 稿面 1897:22629：标签列两行间距 8，右侧下拉宽 240、高 56 */}
                            <Box className="cop-setting-page__field-row cop-setting-page__field-row--qr">
                                <Stack spacing={1} className="cop-setting-page__field-label cop-setting-page__field-label--qr">
                                    <Typography variant="subtitle1" color="text.primary">
                                        {i18n.t('cop_setting_page_qr_code_scan')}
                                    </Typography>
                                    {this.context?.auxiliaryModeEnabled ? (
                                        <Typography variant="body2" color="text.secondary">
                                            {i18n.t('cop_setting_page_qr_code_scan_hint')}
                                        </Typography>
                                    ) : null}
                                </Stack>
                                <Box className="cop-setting-page__field-control cop-setting-page__field-control--select">
                                    <CommonInput
                                        className="cop-setting-page__select"
                                        inputType="SELECT"
                                        size="small"
                                        items={this.getQrScanItems()}
                                        value={String(qrScanMode)}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                            this.onChangeQrScanMode(Number(e.target.value))
                                        }
                                    />
                                </Box>
                            </Box>
                        </Stack>
                    </Box>
                </Box>
            </Box>
        );
    }
}

export default CopSettingPage;
